﻿import * as THREE from 'three';

/**
 * 切线计算工具集。
 * 职责：提供从外部点到圆、圆弧、椭圆的切线切点纯数学计算。
 * 说明：
 * 1. 所有计算在参考平面局部2D坐标系（THREE.Vector2）中进行；
 * 2. 调用方负责世界坐标 ↔ 局部坐标的转换；
 * 3. 所有函数均为纯函数，无副作用。
 */

/**
 * 计算外部点到圆的切线切点（局部2D坐标）。
 * 数学原理：
 * 1. 设P为外部点，C为圆心，r为半径，d=|PC|；
 * 2. 切线与PC方向夹角 α = arcsin(r/d)；
 * 3. 将 PC 单位向量旋转 ±α 得到两条切线方向；
 * 4. 切线长度 = sqrt(d²-r²)，切点 = P + 切线长度 × 旋转方向向量。
 * @param fromLocal 外部点局部2D坐标。
 * @param centerLocal 圆心局部2D坐标。
 * @param radius 圆半径。
 * @returns 切点局部2D坐标数组（外部点在圆内时返回空数组）。
 */
export function computeCircleTangentTouchPointsLocal(
    fromLocal: THREE.Vector2,
    centerLocal: THREE.Vector2,
    radius: number,
): THREE.Vector2[] {
    const dx = centerLocal.x - fromLocal.x;
    const dy = centerLocal.y - fromLocal.y;
    const d = Math.sqrt(dx * dx + dy * dy);

    if (d <= radius + 1e-9) {
        // 点在圆内或圆上，无外切线。
        return [];
    }

    // P 到 C 的单位向量。
    const ux = dx / d;
    const uy = dy / d;

    // 切线与 PC 方向的夹角。
    const sinAlpha = radius / d;
    const cosAlpha = Math.sqrt(1 - sinAlpha * sinAlpha);
    const tangentLen = Math.sqrt(d * d - radius * radius);

    // 将 (ux, uy) 旋转 +α 得到第一条切线方向。
    const t1x = ux * cosAlpha - uy * sinAlpha;
    const t1y = ux * sinAlpha + uy * cosAlpha;

    // 将 (ux, uy) 旋转 -α 得到第二条切线方向。
    const t2x = ux * cosAlpha + uy * sinAlpha;
    const t2y = -ux * sinAlpha + uy * cosAlpha;

    return [
        new THREE.Vector2(fromLocal.x + tangentLen * t1x, fromLocal.y + tangentLen * t1y),
        new THREE.Vector2(fromLocal.x + tangentLen * t2x, fromLocal.y + tangentLen * t2y),
    ];
}

/**
 * 判断角度是否落在弧范围内。
 * 说明：将所有角度规范化到 [0, 2π)，再按旋转方向分情况判断。
 * @param angle 待判断角度（弧度）。
 * @param startAngle 弧起始角（弧度）。
 * @param endAngle 弧终止角（弧度）。
 * @param clockwise 是否顺时针。
 * @returns 角度在弧范围内时返回 true。
 */
function isAngleInArcRange(
    angle: number,
    startAngle: number,
    endAngle: number,
    clockwise: boolean,
): boolean {
    const TWO_PI = Math.PI * 2;
    const normalize = (a: number): number => ((a % TWO_PI) + TWO_PI) % TWO_PI;
    const a = normalize(angle);
    const s = normalize(startAngle);
    const e = normalize(endAngle);

    if (clockwise) {
        // 顺时针：从 s 递减到 e。
        if (s >= e) {
            return a <= s && a >= e;
        } else {
            // 跨越 0 点。
            return a <= s || a >= e;
        }
    } else {
        // 逆时针：从 s 递增到 e。
        if (s <= e) {
            return a >= s && a <= e;
        } else {
            // 跨越 0 点。
            return a >= s || a <= e;
        }
    }
}

/**
 * 计算外部点到圆弧的切线切点（局部2D坐标）。
 * 说明：先计算整圆切点，再过滤出落在弧角度范围内的切点。
 * @param fromLocal 外部点局部2D坐标。
 * @param centerLocal 弧圆心局部2D坐标。
 * @param radius 弧半径。
 * @param startAngle 弧起始角（弧度）。
 * @param endAngle 弧终止角（弧度）。
 * @param clockwise 是否顺时针。
 * @returns 切点局部2D坐标数组（只包含落在弧范围内的切点）。
 */
export function computeArcTangentTouchPointsLocal(
    fromLocal: THREE.Vector2,
    centerLocal: THREE.Vector2,
    radius: number,
    startAngle: number,
    endAngle: number,
    clockwise: boolean,
): THREE.Vector2[] {
    const allPoints = computeCircleTangentTouchPointsLocal(fromLocal, centerLocal, radius);
    return allPoints.filter((tp) => {
        const angle = Math.atan2(tp.y - centerLocal.y, tp.x - centerLocal.x);
        return isAngleInArcRange(angle, startAngle, endAngle, clockwise);
    });
}

/**
 * 计算外部点到椭圆的切线切点（局部2D坐标）。
 * 数学原理：
 * 1. 将外部点变换到椭圆中心为原点、长轴为X轴的坐标系（旋转 -rotation）；
 * 2. 在轴对齐坐标系中，参数方程切点条件为 a·py·sin(t) + b·px·cos(t) = a·b；
 * 3. 该方程形式为 A·sin(t) + B·cos(t) = C，其中 A=a·py, B=b·px, C=a·b；
 * 4. 解为 t = atan2(A,B) ± arccos(C/sqrt(A²+B²))；
 * 5. 将求得的切点旋转回参考平面坐标系并加回中心偏移。
 * @param fromLocal 外部点局部2D坐标。
 * @param centerLocal 椭圆中心局部2D坐标。
 * @param a 椭圆长半轴（radiusX）。
 * @param b 椭圆短半轴（radiusY）。
 * @param rotation 椭圆长轴旋转角（弧度，在参考平面局部空间中）。
 * @returns 切点局部2D坐标数组（点在椭圆内时返回空数组）。
 */
export function computeEllipseTangentTouchPointsLocal(
    fromLocal: THREE.Vector2,
    centerLocal: THREE.Vector2,
    a: number,
    b: number,
    rotation: number,
): THREE.Vector2[] {
    // 平移到椭圆中心为原点。
    const px0 = fromLocal.x - centerLocal.x;
    const py0 = fromLocal.y - centerLocal.y;

    // 旋转到椭圆轴对齐坐标系（旋转 -rotation）。
    const cosNeg = Math.cos(-rotation);
    const sinNeg = Math.sin(-rotation);
    const px = px0 * cosNeg - py0 * sinNeg;
    const py = px0 * sinNeg + py0 * cosNeg;

    // 切线条件方程系数。
    const A = a * py;
    const B = b * px;
    const C = a * b;
    const R = Math.sqrt(A * A + B * B);

    if (R < 1e-9 || C > R + 1e-9) {
        // 点在椭圆内或无解。
        return [];
    }

    const phi = Math.atan2(A, B);
    const delta = Math.acos(Math.min(1, C / R));

    // 旋转回参考平面局部坐标系的角度。
    const cosPos = Math.cos(rotation);
    const sinPos = Math.sin(rotation);

    const touchPoints: THREE.Vector2[] = [];
    for (const t of [phi + delta, phi - delta]) {
        // 椭圆上的点（轴对齐坐标系）。
        const tx = a * Math.cos(t);
        const ty = b * Math.sin(t);

        // 旋转回参考平面局部坐标系。
        const rx = tx * cosPos - ty * sinPos;
        const ry = tx * sinPos + ty * cosPos;

        // 加回中心偏移。
        touchPoints.push(new THREE.Vector2(centerLocal.x + rx, centerLocal.y + ry));
    }
    return touchPoints;
}

//------ 快速相切捕捉数学工具 开始------

/**
 * 计算鼠标位置在圆上的最近投影点（局部2D坐标）。
 * @param mouseLocal 鼠标局部2D坐标。
 * @param centerLocal 圆心局部2D坐标。
 * @param radius 圆半径。
 * @returns 圆上最近点局部2D坐标。
 */
export function closestPointOnCircleLocal(
    mouseLocal: THREE.Vector2,
    centerLocal: THREE.Vector2,
    radius: number,
): THREE.Vector2 {
    const dx = mouseLocal.x - centerLocal.x;
    const dy = mouseLocal.y - centerLocal.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < 1e-9) {
        // 鼠标在圆心：返回正上方点。
        return new THREE.Vector2(centerLocal.x, centerLocal.y + radius);
    }
    return new THREE.Vector2(
        centerLocal.x + (dx / dist) * radius,
        centerLocal.y + (dy / dist) * radius,
    );
}

/**
 * 计算鼠标位置在圆弧上的最近投影点（局部2D坐标）。
 * 说明：若鼠标方向角在弧范围内则直接投影，否则取最近端点。
 * @param mouseLocal 鼠标局部2D坐标。
 * @param centerLocal 圆弧圆心局部2D坐标。
 * @param radius 圆弧半径。
 * @param startAngle 起始角（弧度）。
 * @param endAngle 终止角（弧度）。
 * @param clockwise 是否顺时针。
 * @returns 圆弧上最近点局部2D坐标。
 */
export function closestPointOnArcLocal(
    mouseLocal: THREE.Vector2,
    centerLocal: THREE.Vector2,
    radius: number,
    startAngle: number,
    endAngle: number,
    clockwise: boolean,
): THREE.Vector2 {
    const dx = mouseLocal.x - centerLocal.x;
    const dy = mouseLocal.y - centerLocal.y;
    const mouseAngle = Math.atan2(dy, dx);

    if (isAngleInArcRange(mouseAngle, startAngle, endAngle, clockwise)) {
        // 鼠标方向在弧范围内：直接投影到弧面。
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        return new THREE.Vector2(
            centerLocal.x + (dx / dist) * radius,
            centerLocal.y + (dy / dist) * radius,
        );
    }

    // 鼠标方向超出弧范围：取最近端点。
    const startPt = new THREE.Vector2(
        centerLocal.x + radius * Math.cos(startAngle),
        centerLocal.y + radius * Math.sin(startAngle),
    );
    const endPt = new THREE.Vector2(
        centerLocal.x + radius * Math.cos(endAngle),
        centerLocal.y + radius * Math.sin(endAngle),
    );
    return mouseLocal.distanceTo(startPt) <= mouseLocal.distanceTo(endPt) ? startPt : endPt;
}

/**
 * 计算鼠标位置在椭圆上的最近投影点（局部2D坐标）。
 * 说明：使用 Newton-Raphson 迭代最小化鼠标到椭圆的距离。
 * @param mouseLocal 鼠标局部2D坐标。
 * @param centerLocal 椭圆中心局部2D坐标。
 * @param a 长半轴（radiusX）。
 * @param b 短半轴（radiusY）。
 * @param rotation 长轴旋转角（弧度）。
 * @returns 椭圆上最近点局部2D坐标。
 */
export function closestPointOnEllipseLocal(
    mouseLocal: THREE.Vector2,
    centerLocal: THREE.Vector2,
    a: number,
    b: number,
    rotation: number,
): THREE.Vector2 {
    // 平移到椭圆中心为原点，旋转到轴对齐坐标系。
    const dx = mouseLocal.x - centerLocal.x;
    const dy = mouseLocal.y - centerLocal.y;
    const cosNeg = Math.cos(-rotation);
    const sinNeg = Math.sin(-rotation);
    const px = dx * cosNeg - dy * sinNeg;
    const py = dx * sinNeg + dy * cosNeg;

    // 初始参数猜测：鼠标方向对应的椭圆角。
    let t = Math.atan2(py / (b || 1), px / (a || 1));

    // Newton-Raphson 迭代：最小化到椭圆的平方距离。
    for (let i = 0; i < 12; i++) {
        const ct = Math.cos(t);
        const st = Math.sin(t);
        const ex = a * ct;
        const ey = b * st;
        // 一阶导：d/dt [(ex-px)² + (ey-py)²] / 2
        const f = -a * st * (ex - px) + b * ct * (ey - py);
        // 二阶导
        const df = -a * ct * (ex - px) + a * a * st * st - b * st * (ey - py) + b * b * ct * ct;
        if (Math.abs(df) < 1e-12) break;
        t -= f / df;
    }

    const cosPos = Math.cos(rotation);
    const sinPos = Math.sin(rotation);
    const tx = a * Math.cos(t);
    const ty = b * Math.sin(t);
    // 旋转回参考平面局部坐标系并加回中心偏移。
    return new THREE.Vector2(
        centerLocal.x + tx * cosPos - ty * sinPos,
        centerLocal.y + tx * sinPos + ty * cosPos,
    );
}

/**
 * 计算圆上指定点的切线方向（局部2D坐标，归一化）。
 * 说明：切线垂直于半径方向，取逆时针方向（旋转 +90°）。
 * @param pointLocal 圆上点的局部2D坐标。
 * @param centerLocal 圆心局部2D坐标。
 * @returns 归一化切线方向向量。
 */
export function tangentDirectionOnCircleLocal(
    pointLocal: THREE.Vector2,
    centerLocal: THREE.Vector2,
): THREE.Vector2 {
    const dx = pointLocal.x - centerLocal.x;
    const dy = pointLocal.y - centerLocal.y;
    // 切线 = 半径向量旋转 90°：(-dy, dx)。
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    return new THREE.Vector2(-dy / len, dx / len);
}

/**
 * 计算椭圆上指定点的切线方向（局部2D坐标，归一化）。
 * 说明：椭圆隐式方程 f = x²/a² + y²/b²，法线方向 = ∇f = (x/a², y/b²)，切线垂直于法线。
 * @param pointLocal 椭圆上点的局部2D坐标（参考平面坐标系，含旋转）。
 * @param centerLocal 椭圆中心局部2D坐标。
 * @param a 长半轴（radiusX）。
 * @param b 短半轴（radiusY）。
 * @param rotation 长轴旋转角（弧度）。
 * @returns 归一化切线方向向量（参考平面坐标系）。
 */
export function tangentDirectionOnEllipseLocal(
    pointLocal: THREE.Vector2,
    centerLocal: THREE.Vector2,
    a: number,
    b: number,
    rotation: number,
): THREE.Vector2 {
    // 变换到轴对齐坐标系。
    const dx = pointLocal.x - centerLocal.x;
    const dy = pointLocal.y - centerLocal.y;
    const cosNeg = Math.cos(-rotation);
    const sinNeg = Math.sin(-rotation);
    const px = dx * cosNeg - dy * sinNeg;
    const py = dx * sinNeg + dy * cosNeg;

    // 在轴对齐系中，法线方向 = (px/a², py/b²)，切线 = (-py/b², px/a²)。
    const tx = -py / (b * b || 1);
    const ty = px / (a * a || 1);
    const len = Math.sqrt(tx * tx + ty * ty) || 1;
    const txN = tx / len;
    const tyN = ty / len;

    // 旋转回参考平面局部坐标系。
    const cosPos = Math.cos(rotation);
    const sinPos = Math.sin(rotation);
    return new THREE.Vector2(
        txN * cosPos - tyN * sinPos,
        txN * sinPos + tyN * cosPos,
    );
}

//------ 快速相切捕捉数学工具 结束------
